<template>
  <div>
    <div fluid>
      <base-header class="pb-6 pb-8 pt-5 pt-md-8 bg-gradient-primary header-enhanced">
        <!-- Upload Statistics Cards -->
        <b-row class="mb-4">
          <b-col xl="4" md="6">
            <stats-card
              title="Videos Uploaded"
              type="gradient-primary"
              sub-title="Today: 12"
              icon="ni ni-cloud-upload-96"
              class="mb-4 stats-card-hover"
            >
              <template slot="footer">
                <span class="text-primary mr-2">↗ 25%</span>
                <span class="text-nowrap">vs yesterday</span>
              </template>
            </stats-card>
          </b-col>
          <b-col xl="4" md="6">
            <stats-card
              title="Storage Used"
              type="gradient-info"
              sub-title="2.4 GB"
              icon="ni ni-chart-pie-35"
              class="mb-4 stats-card-hover"
            >
              <template slot="footer">
                <span class="text-info mr-2">● 68%</span>
                <span class="text-nowrap">of 5GB limit</span>
              </template>
            </stats-card>
          </b-col>
          <b-col xl="4" md="6">
            <stats-card
              title="Processing"
              type="gradient-warning"
              sub-title="3 videos"
              icon="ni ni-time-alarm"
              class="mb-4 stats-card-hover"
            >
              <template slot="footer">
                <span class="text-warning mr-2">⟳ Processing</span>
                <span class="text-nowrap">in queue</span>
              </template>
            </stats-card>
          </b-col>
        </b-row>

        <!-- Page Title -->
        <b-row align-v="center" class="mb-3">
          <b-col>
            <h3 class="text-white mb-0">
              <i class="ni ni-cloud-upload-96 mr-2"></i>Upload Video Content
            </h3>
            <p class="text-white-50 mb-0">Upload and configure your video files</p>
          </b-col>
          <b-col class="text-right">
            <b-button
              variant="light"
              size="sm"
              class="shadow-sm"
              @click="$router.go(-1)">
              <i class="ni ni-bold-left mr-1"></i>Back to Videos
            </b-button>
          </b-col>
        </b-row>
      </base-header>

      <div class="container-fluid mt--7">
        <!-- Enhanced Upload Area -->
        <b-card class="mb-4 shadow-lg upload-card border-0">
          <b-row align-v="center" class="mb-3">
            <b-col>
              <h5 class="mb-0 text-dark">
                <i class="ni ni-cloud-upload-96 mr-2 text-primary"></i>
                Video Upload Zone
              </h5>
              <p class="text-muted mb-0">Drag & drop your video files or click to browse</p>
            </b-col>
          </b-row>
          <div class="upload-zone">
            <div id="wistia_uploader" class="wistia-upload-area"></div>
            <div class="upload-overlay" v-if="!uploading">
              <div class="upload-placeholder">
                <i class="ni ni-cloud-upload-96 upload-icon"></i>
                <h4 class="mt-3">Drop your video here</h4>
                <p class="text-muted">or click to browse files</p>
                <small class="text-muted">Supported formats: MP4, MOV, AVI (Max 2GB)</small>
              </div>
            </div>
          </div>
        </b-card>
        <!-- Enhanced Edit Form -->
        <b-card class="shadow-lg edit-form-card border-0" v-if="uploading">
          <div class="card-header bg-gradient-success border-0">
            <h5 class="mb-0 text-white">
              <i class="ni ni-settings mr-2"></i>Configure Video Details
            </h5>
            <small class="text-white-50">Set title and access settings for your video</small>
          </div>
          <div class="card-body">
            <b-row>
              <b-col md="8">
                <b-form-group label="Video Title" label-for="mediaTitle" class="mb-4">
                  <b-form-input
                    id="media_name"
                    placeholder="Enter an engaging video title"
                    autocomplete="off"
                    @input="$v.mediaTitle.$touch()"
                    v-model.trim="$v.mediaTitle.$model"
                    :state="!$v.mediaTitle.$error"
                    size="lg"
                    class="form-control-lg">
                  </b-form-input>
                  <b-form-invalid-feedback>
                    <span v-if="!$v.mediaTitle.required">
                      Video title is required
                    </span>
                  </b-form-invalid-feedback>
                  <small class="form-text text-muted">
                    Choose a descriptive title that will help users find your video
                  </small>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Access Type" label-for="premiumType" class="mb-4">
                  <div class="premium-toggle-container">
                    <toggle-button
                      class="premium-toggle"
                      id="premiumType"
                      v-model="premiumType"
                      :width="140"
                      :height="50"
                      :labels="{
                        checked   : 'Premium',
                        unchecked : 'Free',
                      }"
                      :color="{
                        checked   : '#f5365c',
                        unchecked : '#2dce89'
                      }"/>
                    <small class="form-text text-muted mt-2">
                      {{ premiumType ? 'Only premium users can access' : 'Available to all users' }}
                    </small>
                  </div>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Video Preview Info -->
            <b-row class="mt-4" v-if="uploadedMedia">
              <b-col>
                <div class="video-info-card">
                  <h6 class="mb-3">
                    <i class="ni ni-check-bold text-success mr-2"></i>Video Uploaded Successfully
                  </h6>
                  <b-row>
                    <b-col md="6">
                      <div class="info-item">
                        <i class="ni ni-watch-time text-primary"></i>
                        <span>Duration: {{ formatDuration(uploadedMedia.duration) }}</span>
                      </div>
                    </b-col>
                    <b-col md="6">
                      <div class="info-item">
                        <i class="ni ni-image text-info"></i>
                        <span>Thumbnail: Generated</span>
                      </div>
                    </b-col>
                  </b-row>
                </div>
              </b-col>
            </b-row>

            <!-- Action Buttons -->
            <div class="d-flex justify-content-between align-items-center mt-4">
              <div class="form-status">
                <small class="text-muted" v-if="isFormEdited">
                  <i class="ni ni-check-bold text-success mr-1"></i>Changes detected
                </small>
              </div>
              <div class="action-buttons">
                <b-button
                  variant="outline-danger"
                  @click="onReset"
                  class="mr-3">
                  <i class="ni ni-fat-remove mr-1"></i>Cancel
                </b-button>
                <b-button
                  variant="success"
                  size="lg"
                  @click="onSubmit"
                  :disabled="!$v.mediaTitle.required || isFormEdited === false"
                  class="submit-btn">
                  <i class="ni ni-check-bold mr-1"></i>Save & Publish
                </b-button>
              </div>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Loading -->
      <loading :active.sync="isLoading"
                :is-full-page="true"
                background-color="#000"
                color="#068bcd"
                :opacity="0.3"
                :width="70"
                :height="70">
      </loading>
    </div>
  </div>
</template>
<script>
  const nAxios = () => import('axios');
  const Loading = () => import('vue-loading-overlay');

  import { required } from 'vuelidate/lib/validators';
  import 'vue-loading-overlay/dist/vue-loading.css';
  import baseHeader from '../../BaseHeader';
  import StatsCard from '../../Cards/StatsCard.vue';


  export default {
    name : 'MediaUpload',
    data() {
      return {
        premiumType   : false,
        mediaTitle    : null,
        uploading     : false,
        mediaId       : null,
        wistiaId      : null,
        uploadedMedia : {},
        isFormEdited  : false,
        isLoading     : false,
      };
    },
    components : {
      Loading,
      baseHeader,
      StatsCard
    },
    watch : {
      mediaTitle(value) {
        this.isFormEdited = false;
        if (value !== this.uploadedMedia.name)
          this.isFormEdited = true;
      },
      premiumType(value) {
        this.isFormEdited = false;
        if (value !== false)
          this.isFormEdited = true;
      },
    },
    methods : {

      /**
       * Upload Media
       */
      uploadMedia(media) {
        console.log('2222');
        this.uploadedMedia = media;
        this.mediaTitle = media.name;
        this.wistiaId = media.id;
        this.$http.post('api/media', {
          'mediaTitle'  : media.name,
          'wistiaId'    : media.id,
          'mediaType'   : 'video',
          'premiumType' : 0,
          'mediaLength' : media.duration,
          'thumbnail'   : media.thumbnail.url,
        }).then(res => {
          this.uploading = true;
          this.mediaId = res.data.mediaId;
          this.$notify({
            group : 'notif',
            type  : 'success',
            title : 'Success!',
            text  : 'Uploaded media successfully.',
          });
        }).catch(() => {
          this.$notify({
            group : 'notif',
            type  : 'error',
            title : 'Failed',
            text  : 'Oops! Something went wrong!',
          });
        });
      },

      /**
       * Submit Form
       */
      onSubmit() {
        console.log('Click');
        this.isLoading = true;
        if (!this.$v.mediaTitle.$error) {
          this.$http.put('api/media', {
            'mediaId'      : this.mediaId,
            'updateFields' : {
              'media_title'  : this.mediaTitle,
              'premium_type' : (this.premiumType) ? 1 : 0,
            },
          }).then(() => {
            this.wistiaRequest(); // Update wistia

            this.$notify({
              group : 'notif',
              type  : 'success',
              title : 'Success!',
              text  : 'Saved media successfully.',
            });
          }).catch(() => {
            this.isLoading = false;
            this.$notify({
              group : 'notif',
              type  : 'error',
              title : 'Failed',
              text  : 'Oops! Something went wrong!',
            });
          });
        }
      },

      /**
       * Wistia Request
       */
      wistiaRequest() {
        console.log('11111');
        nAxios({
          method : 'PUT',
          url    : 'https://api.wistia.com/v1/medias/' +
            this.wistiaId + '.json',
          params : {
            'name'         : this.mediaTitle,
            'api_password' : '4a90d7e04bb110d32fadb0edff2fc9a264dda516' +
              'c5a4e7b29b8cab3b97175251',
          },
          headers : {
            'Access-Control-Allow-Origin' : '*',
          },
        }).then(() => {
          this.isLoading = false;
          this.onReset();
          this.$notify({
            group : 'notif',
            type  : 'success',
            title : 'Success!',
            text  : 'Successfully Changed in Wistia!',
          });
        }).catch(() => {
          this.isLoading = false;
          this.$notify({
            group : 'notif',
            type  : 'error',
            title : 'Failed',
            text  : 'Oops! Something went wrong!',
          });
        });
      },

      /**
       * Reset Form
       */
      onReset() {
        window.wistiaUploader.removePreview();
        this.premiumType = false;
        this.mediaTitle = null;
        this.uploading = false;
        this.mediaId = null;
        this.wistiaId = null;
        this.uploadedMedia = {};
      },

      /**
       * Format Duration
       */
      formatDuration(seconds) {
        if (!seconds) return '0:00';
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
      },
    },
    mounted() {
      console.log('3333');
      const wistiaScript = document.createElement('script');
      wistiaScript
        .setAttribute('src',
                      'https://fast.wistia.com/assets/external/api.js');
      document.head.appendChild(wistiaScript);
      window._wq = window._wq || [];
      const vm = this;
      // eslint-disable-next-line no-undef
      _wq.push(function (W) {
        setTimeout(function () {
          window.wistiaUploader = new W.Uploader({
            accessToken : process.env.VUE_APP_WISTIA_ACCESS_TOKEN,
            dropIn      : 'wistia_uploader',
            projectId   : '9365980',
          });

          window.wistiaUploader.bind('uploadsuccess', function (file, media) {
            vm.uploadMedia(media);
          });

          // eslint-disable-next-line no-unused-vars
          window.wistiaUploader.bind('uploadstart', function (file) {
          });

        }, 1000);
      });
    },
    validations : {
      mediaTitle : { required },
    },
  };
</script>
<style scoped>
  @import 'https://fast.wistia.com/assets/external/uploader.css';
</style>
<style lang="scss" scoped>
@import '../../../assets/scss/components/admin/media/media-upload';

/* Enhanced Header Styles */
.baseHeader {
  margin-top: -80px;
  position: relative;
  overflow: hidden;
}

.baseHeader::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(94, 114, 228, 0.9) 0%, rgba(76, 99, 210, 0.9) 100%);
  z-index: 1;
}

.baseHeader::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(94, 114, 228, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(76, 99, 210, 0.2) 0%, transparent 50%);
  z-index: 1;
}

.header-enhanced .card {
  position: relative;
  z-index: 2;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.header-enhanced .text-white {
  color: #fff !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.header-enhanced .card-body {
  position: relative;
  z-index: 2;
}

/* Stats Cards Hover Effects */
.stats-card-hover {
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 12px !important;
}

.stats-card-hover:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}

.stats-card-hover .card-body {
  transition: all 0.3s ease;
}

.stats-card-hover:hover .card-body {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
}

/* Enhanced Upload Card */
.upload-card {
  border-radius: 16px !important;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%);
  animation: fadeInUp 0.8s ease-out;
}

.upload-zone {
  position: relative;
  min-height: 300px;
  border: 3px dashed rgba(94, 114, 228, 0.3);
  border-radius: 12px;
  background: rgba(94, 114, 228, 0.02);
  transition: all 0.3s ease;
}

.upload-zone:hover {
  border-color: rgba(94, 114, 228, 0.5);
  background: rgba(94, 114, 228, 0.05);
}

.wistia-upload-area {
  min-height: 300px;
  position: relative;
  z-index: 10;
}

.upload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 5;
}

.upload-placeholder {
  text-align: center;
  color: rgba(94, 114, 228, 0.6);
}

.upload-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.7;
}

/* Enhanced Edit Form Card */
.edit-form-card {
  border-radius: 16px !important;
  overflow: hidden;
  animation: fadeInUp 0.8s ease-out 0.3s both;
}

.edit-form-card .card-header {
  border-radius: 16px 16px 0 0 !important;
}

.premium-toggle-container {
  text-align: center;
}

.premium-toggle {
  margin: 0 auto;
}

.video-info-card {
  background: rgba(40, 167, 69, 0.1);
  border: 1px solid rgba(40, 167, 69, 0.2);
  border-radius: 8px;
  padding: 1rem;
}

.info-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.info-item i {
  margin-right: 0.5rem;
  width: 20px;
}

.form-status {
  font-size: 0.875rem;
}

.action-buttons .btn {
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.submit-btn {
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
}

.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
}

/* Enhanced Form Controls */
.form-control {
  border-radius: 8px;
  border: 2px solid rgba(94, 114, 228, 0.2);
  transition: all 0.3s ease;
}

.form-control:focus {
  border-color: #5e72e4;
  box-shadow: 0 0 0 0.2rem rgba(94, 114, 228, 0.25);
}

.form-control-lg {
  font-size: 1.1rem;
  padding: 0.75rem 1rem;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .stats-card-hover:hover {
    transform: none;
  }

  .upload-zone {
    min-height: 250px;
  }

  .upload-icon {
    font-size: 3rem;
  }

  .action-buttons {
    flex-direction: column;
    width: 100%;
  }

  .action-buttons .btn {
    margin-bottom: 0.5rem;
    width: 100%;
  }

  .submit-btn:hover {
    transform: none;
  }
}

/* Legacy styles */
.baseHeader, .header {
  margin-top: -80px;
}

table {
  z-index: 99;
  position: relative;
}
</style>
